import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {
  Alert,
  Autocomplete,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { Dialog } from '@/components/AppDialog'
import {
  useCreateGatePassTemplate,
  useEquipmentItems,
  useUpdateGatePassTemplate,
} from '@/api/hooks'
import type { EquipmentItem, GatePassTemplate } from '@/api/types'
import { ErrorBanner, formatNumber } from '@/components/common'

type Line = { equipmentItemId: string; name: string; unit: string; quantity: string }

const REGISTERS = [
  { code: 'OUTDOOR_VESSEL', label: 'Vessel outpass' },
  { code: 'HALL_ASSET', label: 'Hall handover' },
]

const SERVICES = [
  { code: '', label: 'Any job' },
  { code: 'OUTDOOR_CATERING', label: 'Outdoor catering' },
  { code: 'BULK_TAKEAWAY', label: 'Bulk takeaway' },
]

/**
 * Builds a list of what goes out on a kind of job.
 *
 * <p>The items offered are the ones the register can actually send: a vessel
 * outpass is offered vessels and a hall handover is offered hall property. A
 * template naming something its own register cannot carry is one that fails the
 * morning somebody uses it.</p>
 */
export function GatePassTemplateDialog({
  open,
  template,
  onClose,
}: {
  open: boolean
  /** The list being changed. Absent, a new one is built. */
  template?: GatePassTemplate | null
  onClose: () => void
}) {
  const create = useCreateGatePassTemplate()
  const update = useUpdateGatePassTemplate()
  const editing = Boolean(template)

  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [issueType, setIssueType] = useState('OUTDOOR_VESSEL')
  const [serviceType, setServiceType] = useState('')
  const [description, setDescription] = useState('')
  const [lines, setLines] = useState<Line[]>([])
  const [picked, setPicked] = useState<EquipmentItem | null>(null)
  const [quantity, setQuantity] = useState('1')

  // What this register can carry, and nothing else.
  const { data: items } = useEquipmentItems(
    issueType === 'HALL_ASSET' ? 'HALL_ASSET' : 'VESSEL',
  )

  useEffect(() => {
    if (!open) return
    setCode(template?.code ?? '')
    setName(template?.name ?? '')
    setIssueType(template?.issueType ?? 'OUTDOOR_VESSEL')
    setServiceType(template?.serviceType ?? '')
    setDescription(template?.description ?? '')
    setLines((template?.lines ?? []).map((line) => ({
      equipmentItemId: line.equipmentItemId,
      name: line.itemName,
      unit: line.unit,
      quantity: String(line.quantity),
    })))
    setPicked(null)
    setQuantity('1')
  }, [open, template?.id])

  /** The same item twice is two counts of one thing, so it adds up instead. */
  const addLine = () => {
    if (!picked || Number(quantity) <= 0) return
    setLines((current) => {
      const at = current.findIndex((line) => line.equipmentItemId === picked.id)
      if (at === -1) {
        return [...current, {
          equipmentItemId: picked.id,
          name: picked.name,
          unit: picked.unit,
          quantity,
        }]
      }
      return current.map((line, index) =>
        index === at
          ? { ...line, quantity: String(Number(line.quantity) + Number(quantity)) }
          : line,
      )
    })
    setPicked(null)
    setQuantity('1')
  }

  const valid = code.trim() && name.trim() && lines.length > 0

  const submit = () => {
    const body = {
      code: code.trim(),
      name: name.trim(),
      issueType,
      serviceType: serviceType || undefined,
      description: description.trim() || undefined,
      lines: lines.map((line) => ({
        equipmentItemId: line.equipmentItemId,
        quantity: Number(line.quantity),
      })),
    }
    if (template) {
      update.mutate({ id: template.id, body }, { onSuccess: onClose })
      return
    }
    create.mutate(body, { onSuccess: onClose })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{editing ? template?.name : 'New template'}</DialogTitle>
      <DialogContent dividers>
        <ErrorBanner error={create.error ?? update.error} />

        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              required
              label="Code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              placeholder="TPL-WEDDING"
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              required
              label="Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Outdoor wedding - full kit"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Register"
              value={issueType}
              onChange={(event) => {
                setIssueType(event.target.value)
                // The other register carries different things entirely, so
                // what was listed no longer belongs.
                setLines([])
                setPicked(null)
              }}
              helperText="Which pass it is loaded onto"
            >
              {REGISTERS.map((option) => (
                <MenuItem key={option.code} value={option.code}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="For"
              value={serviceType}
              onChange={(event) => setServiceType(event.target.value)}
              helperText="Left as any job, it is always offered"
            >
              {SERVICES.map((option) => (
                <MenuItem key={option.code} value={option.code}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="What it is for"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </Grid>
        </Grid>

        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
          Items
        </Typography>

        {(items ?? []).length === 0 ? (
          <Alert severity="warning">
            Nothing in the store is marked for this register yet. Add items under{' '}
            <strong>Store items</strong> first — a template can only list what the business owns.
          </Alert>
        ) : (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
            <Autocomplete
              size="small"
              options={items ?? []}
              value={picked}
              onChange={(_event, value) => setPicked(value)}
              getOptionLabel={(option) => `${option.code} · ${option.name}`}
              sx={{ flex: 1, minWidth: 240 }}
              renderInput={(params) => <TextField {...params} label="Item" />}
            />
            <TextField
              size="small"
              type="number"
              label="How many"
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') addLine()
              }}
              sx={{ width: 130 }}
            />
            <Button startIcon={<AddIcon />} onClick={addLine} disabled={!picked}>
              Add
            </Button>
          </Stack>
        )}

        {lines.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nothing on this template yet.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell align="right">How many</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((line, index) => (
                <TableRow key={line.equipmentItemId} hover>
                  <TableCell>{line.name}</TableCell>
                  <TableCell align="right">{formatNumber(Number(line.quantity))}</TableCell>
                  <TableCell>{line.unit}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      aria-label={`Remove ${line.name}`}
                      onClick={() => setLines((current) => current.filter((_, at) => at !== index))}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!valid || create.isPending || update.isPending}
        >
          {editing ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
